import { useState } from "react";
import { api } from "../../../api";
import { ErrorNote } from "../../../components/Feedback";
import { Btn, Field, Input, Select, Textarea, Toggle } from "../../../components/Shared";
import ColumnRules from "./ColumnRules";
import DatasetPicker from "./DatasetPicker";
import { FIELD_TYPES, FILE_ROLES, parseExtensions, parseMissing, rulesPatch, rulesState,
         SENSITIVITIES } from "./terms";

// The dictionary entry of one column: Frictionless field (type, format, required/unique,
// missing values) plus title, description (a unit goes into the title or the description, e.g.
// "Sampling depth (m)"), and the rules of its values (ColumnRules, shared with hand-defined
// tables: Frictionless constraints minimum/maximum, lengths, pattern, allowed values). The rules
// shown fit the type; the data check lists the rows that break them
// and Build turns them into CHECK constraints.
// A column whose cells name files gets the logical type "file" with a role, allowed extensions,
// several names per cell (";") and the datasets to look in (DatasetPicker; none chosen: its own
// dataset); the data
// check and Build then link each row to its file. There is no "protocol" type (2026-10-20): a
// protocol is a file, so a column naming protocols is a file column with the role protocol.
// "Personal data" marks the column personal (identifies a person) or sensitive (special
// categories, e.g. health): masked for data readers of the hosted database, left out of exports
// and statistics (backend operate/personal.py). "checked" says it was looked at and is NOT
// personal: nothing is masked, and the "Looks like personal data" warning stops.
// Saving PATCHes /api/columns/{id} with confirmed=true and calls onSaved(column).

export default function ColumnEditor({ column, dataset, onSaved, onCancel }) {
  const c = column;
  const cons = c.constraints || {};
  const [type, setType] = useState(c.type || "string");
  const [format, setFormat] = useState(c.format && c.format !== "default" ? c.format : "");
  const [title, setTitle] = useState(c.title || "");
  const [description, setDescription] = useState(c.description || "");
  const [rules, setRules] = useState(() => rulesState(cons));
  const setRule = key => value => setRules(r => ({ ...r, [key]: value }));
  const [missing, setMissing] = useState((c.missing_values || []).join(", "));
  const [isFile, setIsFile] = useState(c.logical_type === "file");
  const [fileRole, setFileRole] = useState(c.file_role || "");
  const [extensions, setExtensions] = useState((c.file_extensions || []).join(", "));
  const [multiple, setMultiple] = useState(!!c.file_multiple);
  const [scope, setScope] = useState(c.file_scope || []);
  const [sensitivity, setSensitivity] = useState(c.sensitivity || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const save = async e => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const body = {
      type,
      format: format.trim() || "default",
      title: title.trim() || null,
      description: description.trim() || null,
      constraints: rulesPatch(rules, type),
      missing_values: parseMissing(missing),
      logical_type: isFile ? "file" : null,
      ...(isFile ? { file_role: fileRole || null, file_extensions: parseExtensions(extensions),
                     file_multiple: multiple, file_scope: scope } : {}),
      sensitivity: sensitivity || null,
      confirmed: true,
    };
    try {
      const saved = await api.patch(`/columns/${c.id}`, body);
      onSaved(saved);
    } catch (err) {
      setError(err);
      setSaving(false);
    }
  };

  const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 };
  return (
    <form onSubmit={save} aria-label={`Dictionary entry for ${c.name}`}
          style={{ display: "flex", flexDirection: "column", gap: 16, padding: "18px 4px 6px" }}>
      <ErrorNote error={error} />
      <div style={grid}>
        <Field label="Type" hint={c.inferred_type && c.inferred_type !== type ? `Inferred: ${c.inferred_type}` : "Frictionless field type"}>
          <Select value={type} onChange={e => setType(e.target.value || "string")} options={FIELD_TYPES} />
        </Field>
        <Field label="Format" hint="Optional, e.g. %d.%m.%Y for dates or email">
          <Input value={format} onChange={e => setFormat(e.target.value)} placeholder="default" />
        </Field>
        <Field label="Title" hint="A human-readable name">
          <Input value={title} onChange={e => setTitle(e.target.value)} />
        </Field>
        <Field label="Personal data"
               hint="Empty: not personal. Marked columns are masked for data readers, left out of exports, releases and statistics. Checked: looked at, not personal.">
          <Select value={sensitivity} onChange={e => setSensitivity(e.target.value)} options={SENSITIVITIES}
                  aria-label={`Personal data in ${c.name}`} />
        </Field>
      </div>
      <Field label="Description" hint="What the values are, how they were obtained and their unit">
        <Textarea value={description} onChange={e => setDescription(e.target.value)} />
      </Field>
      <ColumnRules type={type} rules={rules} setRule={setRule} columnName={c.name} beside={
        <Field label="Missing-value markers" hint="Comma-separated, e.g. NA, -9999 (empty cells are always missing)">
          <Input value={missing} onChange={e => setMissing(e.target.value)} />
        </Field>
      } />
      <fieldset style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: "12px 14px", margin: 0 }}>
        <legend style={{ fontSize: 14, fontWeight: 600, padding: "0 6px" }}>File reference</legend>
        <span style={{ display: "inline-flex", gap: 8, alignItems: "center", fontSize: 14 }}>
          <Toggle checked={isFile} onChange={setIsFile} label={`${c.name} names files`} />
          The cells are file names: link each row to its file in the project database
        </span>
        {isFile && (
          <div style={{ ...grid, marginTop: 14 }}>
            <Field label="File role" hint="Only files of this role match (empty: any role)">
              <Select value={fileRole} onChange={e => setFileRole(e.target.value)} options={FILE_ROLES} />
            </Field>
            <Field label="Allowed extensions" hint="Comma-separated, e.g. .fastq.gz (empty: any)">
              <Input value={extensions} onChange={e => setExtensions(e.target.value)} />
            </Field>
            <div style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 14, paddingTop: 18 }}>
              <Toggle checked={multiple} onChange={setMultiple} label={`${c.name} holds several file names`} />
              Several names per cell, separated by ;
            </div>
            {dataset && (
              <div style={{ gridColumn: "1 / -1" }}>
                <DatasetPicker projectId={dataset.project_id} currentId={dataset.id} value={scope} onChange={setScope} />
              </div>
            )}
          </div>
        )}
      </fieldset>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <Btn variant="secondary" onClick={onCancel} disabled={saving}>Cancel</Btn>
        <Btn type="submit" disabled={saving}>{saving ? "Saving…" : "Save and confirm"}</Btn>
      </div>
    </form>
  );
}
